/* eslint-disable react-hooks/exhaustive-deps */
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { isEmpty } from 'lodash';
import { useForm, SubmitHandler } from 'react-hook-form';
import { CloseIcon, DeleteIcon, DownIcon, UpIcon } from '../../../shared/components/AppIcons';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2,
  IconButton,
  Stack,
  TextField,
} from '@mui/material';
import RichTextEditorComponent from '../../../shared/components/RichTextEditor';
import { TagType } from '../../../../data/types/tags.interface';
import theme from '../../../../core/theme';
import LocationService from '../services/location.services';
import Toaster from '../../../shared/components/Toaster';

const useStyles = makeStyles({
  paper: {
    minWidth: '412px',
    height: 'auto',
    borderRadius: '4px !important',
    boxShadow: 'rgba(242, 242, 242, 1)',
  },
  header: {
    fontSize: '28px',
  },
  close: {
    fontSize: '50px !important',
    color: '#5F6774 !important',
    '& path': {
      fill: `#2C2C2C !important`,
    },
  },
  input: {
    padding: '8px !important',
    height: '40px',
    border: '1px solid #E1E3E8',
  },
  icon: {
    fontSize: '24px !important',
    '& path': {
      fill: `#F67570 !important`,
    },
  },
  save: {
    height: '38px',
    fontSize: '18px !important',
    boxShadow: 'none !important',
    backgroundColor: '#F67570 !important',
    color: '#fff !important',
    '& g path': {
      fill: '#ffffff !important',
    },
    '&.disable': {
      backgroundColor: `${theme.palette.chipBg.main} !important`,
    },
  },
  cancel: {
    color: '#F67570 !important',
    marginRight: '24px !important',
    fontSize: '16px !important',
    fontFamily: 'Lato !important',
  },
  list: {
    margin: '11px 0px 11px 0px',
    '& .MuiStack-root': {
      border: '1px solid #E1E3E8',
      borderRadius: '4px',
      height: '40px !important',
    },
    '& .MuiInput-underline:before': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:after': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled, .Mui-error):before': {
      borderBottom: 'none',
    },
  },
  dropdownIcon: {
    fontSize: '42px !important',
    '& path': {
      fill: '#5F6774 !important',
    },
  },
});

interface Props {
  setOpenPopup: Dispatch<SetStateAction<boolean>>;
}

const AddLocation: React.FC<Props> = ({ setOpenPopup }) => {
  const classes = useStyles();
  const { register, handleSubmit } = useForm({ mode: 'all' });
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [list, setList] = useState<TagType[]>([]);
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState('');
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState<boolean>(false);
  const [toasterMessage, setToasterMessage] = useState('');

  const getToaster = () => <Toaster messageType="remove" show={show} setShow={setShow} message={`${toasterMessage}`} />;

  const defaultProps = {
    options: list,
    getOptionLabel: (option: TagType) => option.name,
  };

  const getAllLocations = async () => {
    const result = await LocationService.get();
    if (result) {
      setList(result);
    }
  };

  useEffect(() => {
    getAllLocations();
  }, []);

  const onSubmit: SubmitHandler<any> = async (form) => {
    if (!name) {
      setErrorMessage('Please enter the name');
    }
    try {
      const data = { name, description };
      const content = await LocationService.create(data);
      if (content) {
        setOpenPopup(false);
      }
    } catch (error) {
      setErrorMessage(error?.response?.data[0]?.message || 'Please try again');
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
  };

  const handleDelete = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, option: TagType) => {
    const isDeleted = await LocationService.deleteLocations([option.id]);
    if (isDeleted) {
      setShow(true);
      setToasterMessage('Deleted successfully');
    }
  };

  return (
    <Dialog
      open
      aria-modal="true"
      role="dialog"
      onClose={() => setOpenPopup(false)}
      PaperProps={{
        onClick: (e: any) => e.stopPropagation(),
        className: classes.paper,
      }}
    >
      <DialogTitle>
        <Grid2 container justifyContent="space-between" alignItems="center">
          <Grid2>
            <span className={classes.header}>Add Location</span>
          </Grid2>
          <Grid2 container justifyContent="flex-end">
            <IconButton onClick={() => setOpenPopup(false)} role="button" sx={{ padding: 0 }}>
              <CloseIcon viewBox="0 0 42 42" className={classes.close} />
            </IconButton>
          </Grid2>
        </Grid2>
        <Grid2>
          <Divider
            orientation="horizontal"
            flexItem
            sx={{
              backgroundColor: '#E1E3E8',
              paddingBottom: '1px',
            }}
          />
        </Grid2>
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: '0px' }}>
        {show && getToaster()}
        <form onSubmit={handleSubmit(onSubmit)}>
          {errorMessage && errorMessage.length > 0 && (
            <Grid2 container justifyContent="center">
              <div className="text-danger mb-1 pb-5 text-center">{errorMessage}</div>
            </Grid2>
          )}
          <Grid2 container direction="column">
            <Grid2 container direction="column">
              <label>Name*</label>
              <input
                {...register('name', { required: true })}
                name="name"
                id="name"
                className={classes.input}
                onChange={(e) => handleNameChange(e)}
              />
            </Grid2>
            <Grid2 container mt={2} direction="column" className={classes.list}>
              <label>Existing Locations</label>
              <Stack spacing={1}>
                <Autocomplete
                  {...defaultProps}
                  id="auto-highlight"
                  autoHighlight
                  onClose={() => setOpen(false)}
                  popupIcon={
                    open ? <UpIcon className={classes.dropdownIcon} /> : <DownIcon className={classes.dropdownIcon} />
                  }
                  renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                        <span>{option.name}</span>

                        <IconButton size="small" onClick={(e) => handleDelete(e, option)}>
                          <DeleteIcon viewBox="3 5 24 24" className={classes.icon} style={{ cursor: 'pointer' }} />
                        </IconButton>
                      </Stack>
                    </li>
                  )}
                />
              </Stack>
            </Grid2>
            <Grid2 container direction="column" mt={2}>
              <label>Short Description</label>
              <RichTextEditorComponent description={description} handleDescriptionChange={handleDescriptionChange} />
            </Grid2>
          </Grid2>
          <Grid2 container justifyContent="end" m={2} mr={0}>
            <Button
              size="small"
              variant="text"
              type="button"
              onClick={() => setOpenPopup(false)}
              className={classes.cancel}
            >
              cancel
            </Button>
            <Button
              size="small"
              color="primary"
              variant="contained"
              className={`${classes.save} ${!name ? 'disable' : ''}`}
              type="submit"
              disabled={!name || !isEmpty(errorMessage)}
            >
              <div className="caption" style={{ fontSize: 'h5', textTransform: 'none' }}>
                Save
              </div>
            </Button>
          </Grid2>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddLocation;
